import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react"
import { motion, useAnimationControls } from "motion/react"
import {
	IconCheckOutline18 as Check,
	IconChevronLeftOutline18 as ChevronLeft,
	IconChevronRightOutline18 as ChevronRight,
	IconCopyOutline18 as Copy,
	IconDownloadOutline18 as Download,
	IconLinkOutline18 as Link,
	IconMagnifierMinusOutline18 as MinusZoom,
	IconMagnifierPlusOutline18 as PlusZoom,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"
import { createPortal } from "react-dom"

import type { AiImageAnnotation } from "@productlane/lib/src/ai"
import { cn } from "@productlane/lib/src/cn"

import type { IconType } from "../../icons"
import { AnnotationOverlay } from "../ai-chat/annotation-overlay"
import { TooltipProvider } from "../internal/tooltip"
import { Tooltip } from "../tooltip"
import { useImageViewerHost } from "./image-viewer-host"

export interface ViewerImage {
	src: string
	alt?: string
	name?: string
	width?: number
	height?: number
	annotations?: AiImageAnnotation[]
}

interface ImageViewerProps {
	images: ViewerImage[]
	index: number
	onIndexChange: (index: number) => void
	onClose: () => void
	originRect?: DOMRect | null
}

interface FlipTransform {
	x: number
	y: number
	scale: number
}

function flipFromRects(origin: DOMRect, target: DOMRect): FlipTransform {
	const scale = origin.width / target.width || 1
	const originCx = origin.left + origin.width / 2
	const originCy = origin.top + origin.height / 2
	const targetCx = target.left + target.width / 2
	const targetCy = target.top + target.height / 2
	return { x: originCx - targetCx, y: originCy - targetCy, scale }
}

const MIN_ZOOM = 1
const MAX_ZOOM = 5
const ZOOM_STEP = 0.5

export function ImageViewer({
	images,
	index,
	onIndexChange,
	onClose,
	originRect,
}: ImageViewerProps) {
	const host = useImageViewerHost()
	const container = host?.container ?? null
	const compact = host?.compact ?? false
	// The host card resizes while the viewer mounts, so a FLIP measured against a
	// stale target rect drifts. Fade instead.
	const flipOrigin = compact ? null : (originRect ?? null)
	const image = images[index]
	const [zoom, setZoom] = useState(1)
	const [copiedImage, setCopiedImage] = useState(false)
	const [copiedLink, setCopiedLink] = useState(false)
	const offsetRef = useRef({ x: 0, y: 0 })
	const imgRef = useRef<HTMLImageElement>(null)
	const boundsRef = useRef<HTMLDivElement>(null)
	const [fit, setFit] = useState<{ width: number; height: number } | null>(null)
	const zoomRef = useRef<HTMLDivElement>(null)
	const flipRef = useRef<HTMLDivElement>(null)
	const flipControls = useAnimationControls()
	const backdropControls = useAnimationControls()
	const chromeControls = useAnimationControls()
	const didFlipIn = useRef(false)
	const closingRef = useRef(false)
	const flipRectRef = useRef<DOMRect | null>(null)
	const initialIndexRef = useRef(index)

	// Percentage max-height dies on the auto-height wrappers between the padded
	// box and the image, so the fitting box is passed down in pixels instead.
	useLayoutEffect(() => {
		const el = boundsRef.current
		if (!el) return
		const measure = () => {
			const style = getComputedStyle(el)
			const padX =
				parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
			const padY =
				parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
			setFit({
				width: Math.max(0, el.clientWidth - padX),
				height: Math.max(0, el.clientHeight - padY),
			})
		}
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(el)
		return () => observer.disconnect()
	}, [])

	// FLIP: snap the image onto the origin thumbnail, then spring it to center
	const flipIn = useCallback(() => {
		if (didFlipIn.current || !flipOrigin || !flipRef.current) return
		const target = flipRef.current.getBoundingClientRect()
		if (target.width === 0) return
		flipRectRef.current = target
		didFlipIn.current = true
		const from = flipFromRects(flipOrigin, target)
		flipControls.set({ x: from.x, y: from.y, scale: from.scale })
		void flipControls.start({
			x: 0,
			y: 0,
			scale: 1,
			transition: { type: "spring", duration: 0.5, bounce: 0.18 },
		})
	}, [flipOrigin, flipControls])

	useLayoutEffect(() => {
		if (!flipOrigin) {
			flipControls.set({ opacity: 0, scale: 0.96 })
			void flipControls.start({
				opacity: 1,
				scale: 1,
				transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
			})
			return
		}
		flipIn()
	}, [flipOrigin, flipIn, flipControls])

	useLayoutEffect(() => {
		void backdropControls.start({
			opacity: 1,
			transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] },
		})
		void chromeControls.start({
			opacity: 1,
			transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1], delay: 0.1 },
		})
	}, [backdropControls, chromeControls])

	const requestClose = useCallback(() => {
		if (closingRef.current) return
		closingRef.current = true
		const sameImage = index === initialIndexRef.current
		const from =
			sameImage && flipOrigin && flipRectRef.current
				? flipFromRects(flipOrigin, flipRectRef.current)
				: null
		const backdrop = backdropControls.start({
			opacity: 0,
			transition: { duration: 0.25, ease: [0.23, 1, 0.32, 1] },
		})
		void chromeControls.start({
			opacity: 0,
			transition: { duration: 0.1, ease: "easeOut" },
		})
		const flip = from
			? flipControls.start({
					x: from.x,
					y: from.y,
					scale: from.scale,
					transition: { type: "spring", duration: 0.45, bounce: 0 },
				})
			: flipControls.start({
					opacity: 0,
					scale: 0.97,
					transition: { duration: 0.25, ease: [0.23, 1, 0.32, 1] },
				})
		void Promise.all([backdrop, flip]).then(onClose)
	}, [
		flipOrigin,
		index,
		flipControls,
		backdropControls,
		chromeControls,
		onClose,
	])

	// Zoom and pan land on the wrapper so an annotation overlay scales with
	// the image instead of staying pinned to the unzoomed box.
	const applyTransform = useCallback((nextZoom: number) => {
		const el = zoomRef.current
		if (!el) return
		el.style.transform = `translate(${offsetRef.current.x}px, ${offsetRef.current.y}px) scale(${nextZoom})`
	}, [])

	const resetView = useCallback(() => {
		offsetRef.current = { x: 0, y: 0 }
		setZoom(1)
	}, [])

	useLayoutEffect(() => {
		resetView()
	}, [index, resetView])

	useLayoutEffect(() => {
		applyTransform(zoom)
	}, [zoom, applyTransform])

	const goTo = useCallback(
		(next: number) => {
			if (next < 0 || next > images.length - 1) return
			onIndexChange(next)
		},
		[images.length, onIndexChange],
	)

	useEffect(() => {
		const handledKeys = new Set([
			"Escape",
			"ArrowLeft",
			"ArrowRight",
			"+",
			"=",
			"-",
		])
		const onKeyDown = (e: KeyboardEvent) => {
			if (!handledKeys.has(e.key)) return
			// Stop the open thread/inbox shortcuts from also acting on this key
			e.preventDefault()
			e.stopPropagation()
			switch (e.key) {
				case "Escape":
					requestClose()
					break
				case "ArrowLeft":
					goTo(index - 1)
					break
				case "ArrowRight":
					goTo(index + 1)
					break
				case "+":
				case "=":
					setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))
					break
				case "-":
					setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))
					break
			}
		}
		document.addEventListener("keydown", onKeyDown, true)
		return () => document.removeEventListener("keydown", onKeyDown, true)
	}, [index, goTo, requestClose])

	const hostRef = useRef(host)
	hostRef.current = host
	useEffect(() => {
		hostRef.current?.onOpen?.()
		return () => hostRef.current?.onClose?.()
	}, [])

	useEffect(() => {
		const onWheel = (e: WheelEvent) => {
			if (!e.ctrlKey && !e.metaKey) return
			e.preventDefault()
			setZoom((z) =>
				Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z - e.deltaY * 0.01)),
			)
		}
		window.addEventListener("wheel", onWheel, { passive: false })
		return () => window.removeEventListener("wheel", onWheel)
	}, [])

	const handleDownload = useCallback(async () => {
		if (!image) return
		try {
			const res = await fetch(image.src)
			const blob = await res.blob()
			const url = URL.createObjectURL(blob)
			const a = document.createElement("a")
			a.href = url
			a.download = image.name || "image"
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			URL.revokeObjectURL(url)
		} catch {
			window.open(image.src, "_blank", "noopener,noreferrer")
		}
	}, [image])

	const handleCopyImage = useCallback(async () => {
		if (!image) return
		try {
			const res = await fetch(image.src)
			const blob = await res.blob()
			await navigator.clipboard.write([
				new ClipboardItem({ [blob.type]: blob }),
			])
			setCopiedImage(true)
			setTimeout(() => setCopiedImage(false), 1500)
		} catch {
			// clipboard image write unsupported; ignore
		}
	}, [image])

	const handleCopyLink = useCallback(async () => {
		if (!image) return
		try {
			await navigator.clipboard.writeText(image.src)
			setCopiedLink(true)
			setTimeout(() => setCopiedLink(false), 1500)
		} catch {
			// ignore
		}
	}, [image])

	const isPannable = zoom > 1
	const dragState = useRef<{
		startX: number
		startY: number
		baseX: number
		baseY: number
	} | null>(null)

	const onPointerDown = (e: React.PointerEvent) => {
		if (!isPannable) return
		dragState.current = {
			startX: e.clientX,
			startY: e.clientY,
			baseX: offsetRef.current.x,
			baseY: offsetRef.current.y,
		}
		;(e.target as HTMLElement).setPointerCapture(e.pointerId)
	}

	const onPointerMove = (e: React.PointerEvent) => {
		if (!dragState.current) return
		offsetRef.current = {
			x: dragState.current.baseX + (e.clientX - dragState.current.startX),
			y: dragState.current.baseY + (e.clientY - dragState.current.startY),
		}
		applyTransform(zoom)
	}

	const onPointerUp = () => {
		dragState.current = null
	}

	if (!image) return null

	const hasMultiple = images.length > 1
	const displayName = image.name || filenameFromSrc(image.src)

	return createPortal(
		<TooltipProvider delayDuration={200}>
			{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
			<div
				role="dialog"
				aria-modal="true"
				className={cn(
					"inset-0 z-[1000] flex flex-col",
					container ? "absolute" : "fixed",
				)}
				onClick={requestClose}
			>
				<motion.div
					className="absolute inset-0 bg-white/85 backdrop-blur-md dark:bg-gray-950/85"
					initial={{ opacity: flipOrigin ? 0 : 1 }}
					animate={backdropControls}
				/>
				<motion.div
					className={cn(
						"absolute right-3 top-3 z-10 flex items-center gap-1 rounded-lg bg-white/90 p-1 shadow-sm ring-1 ring-black/5 backdrop-blur dark:bg-gray-900/90 dark:ring-white/10",
						compact && "gap-0.5 p-0.5",
					)}
					onClick={(e) => e.stopPropagation()}
					initial={{ opacity: 0 }}
					animate={chromeControls}
				>
					<span
						className={cn(
							"min-w-[3rem] select-none px-2 text-center text-sm tabular-nums text-gray-500 dark:text-gray-400",
							compact && "min-w-[2.5rem] px-1 text-xs",
						)}
					>
						{Math.round(zoom * 100)}%
					</span>
					<ToolbarButton
						label="Zoom out"
						Icon={MinusZoom}
						disabled={zoom <= MIN_ZOOM}
						onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
					/>
					<ToolbarButton
						label="Zoom in"
						Icon={PlusZoom}
						disabled={zoom >= MAX_ZOOM}
						onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
					/>
					<div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700/80" />
					<ToolbarButton
						label="Download"
						Icon={Download}
						onClick={handleDownload}
					/>
					<ToolbarButton
						label={copiedImage ? "Copied" : "Copy image"}
						Icon={copiedImage ? Check : Copy}
						onClick={handleCopyImage}
					/>
					<ToolbarButton
						label={copiedLink ? "Copied" : "Copy link"}
						Icon={copiedLink ? Check : Link}
						onClick={handleCopyLink}
					/>
					<div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700/80" />
					<ToolbarButton label="Close" Icon={X} onClick={requestClose} />
				</motion.div>

				{hasMultiple && (
					<>
						<NavButton
							side="left"
							disabled={index === 0}
							onClick={() => goTo(index - 1)}
						/>
						<NavButton
							side="right"
							disabled={index === images.length - 1}
							onClick={() => goTo(index + 1)}
						/>
					</>
				)}

				<div
					ref={boundsRef}
					className={cn(
						"pointer-events-none relative z-[1] flex min-h-0 flex-1 items-center justify-center overflow-hidden",
						compact ? "px-3 pb-10 pt-12" : "p-16",
					)}
				>
					<motion.div
						ref={flipRef}
						animate={flipControls}
						className="pointer-events-auto flex max-h-full min-h-0 max-w-full items-center justify-center will-change-transform"
						style={{ transformOrigin: "center center" }}
					>
						{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
						<div
							ref={zoomRef}
							onClick={(e) => e.stopPropagation()}
							onPointerDown={onPointerDown}
							onPointerMove={onPointerMove}
							onPointerUp={onPointerUp}
							onPointerCancel={onPointerUp}
							className={cn(
								"relative flex max-h-full max-w-full transition-transform duration-150 will-change-transform",
								isPannable
									? "cursor-grab active:cursor-grabbing"
									: "cursor-zoom-out",
							)}
							style={{ transformOrigin: "center center" }}
						>
							<img
								ref={imgRef}
								src={image.src}
								alt={image.alt ?? displayName ?? ""}
								draggable={false}
								onLoad={flipIn}
								className="max-h-full max-w-full select-none object-contain"
								style={
									fit
										? { maxWidth: fit.width, maxHeight: fit.height }
										: undefined
								}
							/>
							{image.annotations && image.annotations.length > 0 && (
								<AnnotationOverlay
									annotations={image.annotations}
									width={image.width && image.width > 0 ? image.width : 1200}
									height={image.height && image.height > 0 ? image.height : 800}
								/>
							)}
						</div>
					</motion.div>
				</div>

				<motion.div
					className={cn(
						"pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400",
						compact ? "px-3 py-2 text-xs" : "p-4",
					)}
					initial={{ opacity: 0 }}
					animate={chromeControls}
				>
					<span className="max-w-[60%] truncate font-medium text-gray-700 dark:text-gray-200">
						{displayName}
					</span>
					{hasMultiple && (
						<span className="tabular-nums text-gray-400 dark:text-gray-500">
							{index + 1} / {images.length}
						</span>
					)}
				</motion.div>
			</div>
		</TooltipProvider>,
		container ?? document.body,
	)
}

function filenameFromSrc(src: string): string {
	if (src.startsWith("data:")) return "Image"
	try {
		const { pathname } = new URL(src, "http://x")
		const last = decodeURIComponent(pathname.split("/").pop() ?? "")
		return last || "Image"
	} catch {
		return "Image"
	}
}

function ToolbarButton({
	label,
	Icon,
	onClick,
	disabled,
}: {
	label: string
	Icon: IconType
	onClick: () => void
	disabled?: boolean
}) {
	return (
		<Tooltip label={label}>
			<button
				type="button"
				aria-label={label}
				disabled={disabled}
				onClick={onClick}
				className="flex h-7 w-7 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
			>
				<Icon className="h-4 w-4" />
			</button>
		</Tooltip>
	)
}

function NavButton({
	side,
	onClick,
	disabled,
}: {
	side: "left" | "right"
	onClick: () => void
	disabled?: boolean
}) {
	const Icon = side === "left" ? ChevronLeft : ChevronRight
	return (
		<button
			type="button"
			aria-label={side === "left" ? "Previous image" : "Next image"}
			disabled={disabled}
			onClick={(e) => {
				e.stopPropagation()
				onClick()
			}}
			className={cn(
				"absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-sm ring-1 ring-black/5 backdrop-blur transition-colors hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30 dark:bg-gray-900/90 dark:text-gray-200 dark:ring-white/10 dark:hover:bg-gray-800",
				side === "left" ? "left-4" : "right-4",
			)}
		>
			<Icon className="h-5 w-5" />
		</button>
	)
}
